import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

/**
 * Async-tolerant string KV store. Native reads/writes return promises (Preferences); web
 * returns synchronously (`localStorage`). Callers must `await` either way.
 */
export type DeviceKeyValueStorage = {
    getItem(key: string): string | null | Promise<string | null>
    setItem(key: string, value: string): void | Promise<void>
    removeItem(key: string): void | Promise<void>
}

/**
 * True on native iOS/Android where `@capacitor/preferences` writes to the device keychain /
 * SharedPreferences. Web (including Capacitor live-reload in a browser) uses `localStorage`.
 */
function shouldUseCapacitorPreferences(): boolean {
    if (import.meta.server) {
        return false
    }
    try {
        const platform = Capacitor.getPlatform()
        return platform === 'ios' || platform === 'android'
    } catch {
        return false
    }
}

/**
 * No-op storage for SSR / non-browser contexts so callers can resolve reads without touching
 * Preferences or `localStorage`.
 */
function createNoopStorage(): DeviceKeyValueStorage {
    return {
        getItem: async () => null,
        setItem: async () => {},
        removeItem: async () => {},
    }
}

/**
 * Device-durable KV backend: Capacitor Preferences on native, `localStorage` on web.
 *
 * Survives app relaunch (unlike `sessionStorage`), so use it for caches that must be warm on the
 * first frame after a cold start. All failures degrade to null / no-op rather than throwing.
 */
export function createDeviceKeyValueStorage(): DeviceKeyValueStorage {
    if (import.meta.server || typeof window === 'undefined') {
        return createNoopStorage()
    }

    if (shouldUseCapacitorPreferences()) {
        return {
            async getItem(key) {
                const { value } = await Preferences.get({ key })
                return value
            },
            async setItem(key, value) {
                await Preferences.set({ key, value })
            },
            async removeItem(key) {
                await Preferences.remove({ key })
            },
        }
    }

    return {
        getItem(key) {
            try {
                return window.localStorage.getItem(key)
            } catch {
                return null
            }
        },
        setItem(key, value) {
            try {
                window.localStorage.setItem(key, value)
            } catch {
                /* Quota / private mode — best-effort */
            }
        },
        removeItem(key) {
            try {
                window.localStorage.removeItem(key)
            } catch {
                /* Best-effort */
            }
        },
    }
}
